/**
 * The date windows a filter offers — "last 7 days", "this quarter", "year to
 * date" — the custom range behind them, and the arithmetic that turns any of
 * them into two days.
 *
 * A preset is stored as a preset, not as the two dates it happens to mean
 * today. That is the whole reason this exists: a saved view called "invoiced
 * last month" that froze into `between 2026-08-01 and 2026-08-31` the moment
 * it was saved is a view that quietly stops being true in September, and
 * nothing about it looks wrong. The server resolves the preset when it runs
 * the query.
 *
 * `periodDays` and `relativeDays` live here rather than in the list engine
 * because **the picker and the query have to agree** (v3 §5.2): the rail
 * shows "Aug 23 – Sep 21" beside "Last 30 days" and the calendar paints that
 * range, and a second implementation on the client is a second answer to one
 * question. They are pure string arithmetic — `shared/src/calendar.ts` — so
 * there was never a reason for them to be server-only.
 */
import { z } from 'zod';
import { addBucket, addDays, addMonths, startOf } from './calendar.js';
export const DATE_PRESETS = [
    'today', 'yesterday',
    'last_7_days', 'last_30_days',
    'next_7_days', 'next_30_days',
    'last_week', 'this_week', 'next_week',
    'last_month', 'this_month', 'next_month', 'month_to_date',
    'last_quarter', 'this_quarter', 'next_quarter', 'quarter_to_date',
    'last_year', 'this_year', 'next_year', 'year_to_date',
];
export const DATE_PRESET_LABELS = {
    today: 'Today',
    yesterday: 'Yesterday',
    last_7_days: 'Last 7 days',
    last_30_days: 'Last 30 days',
    next_7_days: 'Next 7 days',
    next_30_days: 'Next 30 days',
    last_week: 'Last week',
    this_week: 'This week',
    next_week: 'Next week',
    last_month: 'Last month',
    this_month: 'This month',
    next_month: 'Next month',
    month_to_date: 'Month to date',
    last_quarter: 'Last quarter',
    this_quarter: 'This quarter',
    next_quarter: 'Next quarter',
    quarter_to_date: 'Quarter to date',
    last_year: 'Last year',
    this_year: 'This year',
    next_year: 'Next year',
    year_to_date: 'Year to date',
};
/**
 * How the presets are grouped in the picker (v3 §5.2, board 5).
 *
 * **Past · Now · Coming**, and then everything else behind "More windows ›".
 * Grouped by where the window sits relative to today rather than by calendar
 * unit, because that is the question somebody has when they open it — the
 * old Weeks/Months/Quarters/Years grouping put "last week" and "next week"
 * side by side, which are opposite answers.
 *
 * Nothing is dropped from `More`: a view saved last year may say "quarter to
 * date", and a preset nothing offers is a filter nobody can change.
 */
export const DATE_PRESET_GROUPS = [
    {
        label: 'Past',
        presets: ['today', 'yesterday', 'last_7_days', 'last_30_days', 'last_month', 'last_quarter'],
    },
    { label: 'Now', presets: ['this_week', 'this_month', 'month_to_date', 'this_year'] },
    { label: 'Coming', presets: ['next_7_days', 'next_30_days', 'next_month'] },
    {
        label: 'More',
        presets: [
            'last_week', 'next_week',
            'this_quarter', 'next_quarter', 'quarter_to_date',
            'last_year', 'next_year', 'year_to_date',
        ],
    },
];
/** A `YYYY-MM-DD` day, which is the only kind of date a filter ever carries. */
export const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Not a date');
/**
 * What an `in_period` condition holds: a preset, or the two days of a custom
 * range. Both inclusive — "between the 1st and the 31st" includes both.
 */
export const datePeriod = z.union([
    z.object({ preset: z.enum(DATE_PRESETS) }),
    z.object({ from: day, to: day }),
]);
export const isPreset = (period) => 'preset' in period;
/** How a period reads on a chip: "Last 30 days", or "Mar 1 – Mar 15". */
export function describePeriod(period, today) {
    if (isPreset(period))
        return DATE_PRESET_LABELS[period.preset];
    return describeDayRange(period.from, period.to, today);
}
const MONTH_DAY = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
});
/**
 * Two days as a reader wants them: `Aug 23 – Sep 21`.
 *
 * **The year appears only on an end that is not in the current one**
 * (`Aug 23, 2025 – Sep 21`), because a range inside this year is read a
 * hundred times a day and "2026" on both ends of it is noise. One open end
 * reads as a bound rather than a range, which is what `gte`/`lte` are.
 */
export function describeDayRange(from, to, today) {
    const thisYear = (today ?? new Date().toISOString().slice(0, 10)).slice(0, 4);
    const one = (value) => {
        const date = new Date(`${value}T12:00:00Z`);
        if (Number.isNaN(date.getTime()))
            return value;
        const words = MONTH_DAY.format(date);
        return value.slice(0, 4) === thisYear ? words : `${words}, ${value.slice(0, 4)}`;
    };
    if (from && to)
        return from === to ? one(from) : `${one(from)} – ${one(to)}`;
    if (from)
        return `from ${one(from)}`;
    if (to)
        return `up to ${one(to)}`;
    return 'any date';
}
/**
 * The two days a period covers, resolved against the caller's today.
 *
 * Every preset is inclusive of both ends, and the "last N days" family
 * **counts today as one of the N** — thirty days back from the 21st is the
 * 23rd of the month before, not the 22nd. That is also what `in_relative`'s
 * `past` means, which is the point: the count-back row and the preset beside
 * it have to describe the same fortnight or the picker contradicts itself.
 */
export function periodDays(period, today) {
    if (!isPreset(period))
        return { from: period.from, to: period.to };
    const whole = (unit, offset) => {
        const start = addBucket(startOf(today, unit), unit, offset);
        return { from: start, to: addDays(addBucket(start, unit, 1), -1) };
    };
    const toDate = (unit) => ({ from: startOf(today, unit), to: today });
    const answers = {
        today: () => ({ from: today, to: today }),
        yesterday: () => ({ from: addDays(today, -1), to: addDays(today, -1) }),
        last_7_days: () => ({ from: addDays(today, -6), to: today }),
        last_30_days: () => ({ from: addDays(today, -29), to: today }),
        next_7_days: () => ({ from: today, to: addDays(today, 6) }),
        next_30_days: () => ({ from: today, to: addDays(today, 29) }),
        last_week: () => whole('week', -1),
        this_week: () => whole('week', 0),
        next_week: () => whole('week', 1),
        last_month: () => whole('month', -1),
        this_month: () => whole('month', 0),
        next_month: () => whole('month', 1),
        month_to_date: () => toDate('month'),
        last_quarter: () => whole('quarter', -1),
        this_quarter: () => whole('quarter', 0),
        next_quarter: () => whole('quarter', 1),
        quarter_to_date: () => toDate('quarter'),
        last_year: () => whole('year', -1),
        this_year: () => whole('year', 0),
        next_year: () => whole('year', 1),
        year_to_date: () => toDate('year'),
    };
    return answers[period.preset]();
}
/**
 * The two days an `in_relative` window covers.
 *
 * **`past` counts today as one of the N** (v3 §5.1): thirty days means
 * thirty days *including today*, so `{days, 30, past}` is exactly
 * `last_30_days` and the count-back row and the preset above it cannot
 * disagree. It used to go a full N back from today, which is N + 1 days and
 * a range one day wider than the preset it was meant to match. `future`
 * mirrors it. A month is a month rather than thirty days, so `from` is N
 * months back **plus a day** — the same off-by-one, for the same reason.
 *
 * `ago` has no lower bound, which is the whole of what it is for: "invoiced
 * more than 45 days ago" is a collections list, and giving it a floor would
 * silently drop the oldest debts — the ones that matter most. It is measured
 * a full N back, not N − 1, because it is a threshold rather than a window.
 */
export function relativeDays(window, today) {
    const days = window.unit === 'days' ? window.amount
        : window.unit === 'weeks' ? window.amount * 7
            : null;
    switch (window.direction) {
        case 'past': return {
            from: days === null
                ? addDays(addMonths(today, -window.amount), 1)
                : addDays(today, -(days - 1)),
            to: today,
        };
        case 'future': return {
            from: today,
            to: days === null
                ? addDays(addMonths(today, window.amount), -1)
                : addDays(today, days - 1),
        };
        case 'ago': return {
            from: null,
            to: days === null ? addMonths(today, -window.amount) : addDays(today, -days),
        };
    }
}
//# sourceMappingURL=date-periods.js.map